import { useEffect, useMemo, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { getPortalSchedule, type ScheduleEvent } from '@lib/api/portal';
import apiClient from '@lib/api/client';
import { CalendarSubscribe } from '@/components/scheduling/CalendarSubscribe';

// Local YYYY-MM-DD (avoids UTC off-by-one).
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const openMaps = (address: string) =>
  window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');

const day = (s?: string | null) => (s || '').slice(0, 10);

export const ScheduleSection = () => {
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [monthAnchor, setMonthAnchor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [selectedDay, setSelectedDay] = useState<string>(() => ymd(new Date()));
  const [ganttOpen, setGanttOpen] = useState(false);
  // Task-suggestion clearance (set per-employee by the office)
  const [canSuggest, setCanSuggest] = useState(false);
  interface Delivery { id: number; po_number: string; expected_date: string; vendor?: string; job_title?: string }
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [sTitle, setSTitle] = useState('');
  const [sJobId, setSJobId] = useState<number | null>(null);
  const [sMsg, setSMsg] = useState('');
  const today = ymd(new Date());

  const loadSchedule = useCallback(async () => {
    try {
      setEvents(await getPortalSchedule());
      setError(null);
    } catch {
      setError('Failed to load schedule. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);
  useEffect(() => {
    apiClient.get('/portal/me/permissions')
      .then(r => setCanSuggest(!!r.data.data?.can_suggest_tasks))
      .catch(() => {});
    apiClient.get('/portal/me/deliveries')
      .then(r => setDeliveries(r.data.data ?? []))
      .catch(() => {});
  }, []);

  const suggestJobs = useMemo(() => {
    const m = new Map<number, string>();
    for (const e of events) if (e.job_id) m.set(e.job_id, e.job_title || `Job ${e.job_id}`);
    return [...m.entries()].map(([id, title]) => ({ id, title }));
  }, [events]);

  const sendSuggestion = async (dateKey: string) => {
    const title = sTitle.trim();
    const jobId = sJobId ?? suggestJobs[0]?.id;
    if (!title || !jobId) return;
    setSMsg('');
    try {
      await apiClient.post('/portal/me/tasks', { job_id: jobId, date: dateKey, title });
      setSTitle('');
      setSuggestOpen(false);
      setSMsg('Sent — the office will review it.');
      loadSchedule();
    } catch {
      setSMsg('Could not send the suggestion.');
    }
  };

  // Events per day (multi-day events cover their whole range)
  const eventsByDay = useMemo(() => {
    const m = new Map<string, ScheduleEvent[]>();
    for (const e of events) {
      const start = day(e.scheduled_date);
      if (!start) continue;
      const end = day(e.end_date) || start;
      const d = new Date(start + 'T12:00:00');
      const stop = new Date(end + 'T12:00:00');
      let guard = 0;
      while (d <= stop && guard++ < 120) {
        const key = ymd(d);
        const arr = m.get(key) || [];
        arr.push(e);
        m.set(key, arr);
        d.setDate(d.getDate() + 1);
      }
    }
    return m;
  }, [events]);

  // Monday-first month grid
  const weeks = useMemo(() => {
    const y = monthAnchor.getFullYear(), mo = monthAnchor.getMonth();
    const offset = new Date(y, mo, 1).getDay(); // Sunday-first (S M T W T F S)
    const cursor = new Date(y, mo, 1 - offset);
    const rows: Date[][] = [];
    for (let w = 0; w < 6; w++) {
      const row: Date[] = [];
      for (let i = 0; i < 7; i++) { row.push(new Date(cursor)); cursor.setDate(cursor.getDate() + 1); }
      rows.push(row);
    }
    return rows;
  }, [monthAnchor]);

  const curMonth = monthAnchor.getMonth();
  const daysInMonth = new Date(monthAnchor.getFullYear(), curMonth + 1, 0).getDate();

  // Gantt rows: events overlapping the visible month
  const ganttRows = useMemo(() => {
    const mStart = ymd(new Date(monthAnchor.getFullYear(), curMonth, 1));
    const mEnd = ymd(new Date(monthAnchor.getFullYear(), curMonth, daysInMonth));
    return events
      .filter(e => {
        const s = day(e.scheduled_date); if (!s) return false;
        const en = day(e.end_date) || s;
        return s <= mEnd && en >= mStart;
      })
      .map(e => {
        const s = day(e.scheduled_date);
        const en = day(e.end_date) || s;
        const startDay = s < mStart ? 1 : new Date(s + 'T12:00:00').getDate();
        const endDay = en > mEnd ? daysInMonth : new Date(en + 'T12:00:00').getDate();
        return { e, startDay, endDay };
      })
      .sort((a, b) => a.startDay - b.startDay);
  }, [events, monthAnchor, curMonth, daysInMonth]);

  // Planner tasks per day. A task counts on a day when:
  //  • its own date RANGE covers the day (start_date..end_date), or
  //  • it has no date but its job runs that day ("any day" tasks).
  const tasksByDay = useMemo(() => {
    const m = new Map<string, { task: NonNullable<ScheduleEvent['subtasks']>[number]; job: string }[]>();
    for (const e of events) {
      for (const st of e.subtasks || []) {
        const start = day(st.start_date);
        if (!start) continue;
        const end = day(st.end_date) || start;
        const cursor = new Date(start + 'T12:00:00');
        const stop = new Date(end + 'T12:00:00');
        let guard = 0;
        while (cursor <= stop && guard++ < 62) {
          const key = ymd(cursor);
          const arr = m.get(key) || [];
          if (!arr.some(x => x.task.id === st.id)) arr.push({ task: st, job: e.job_title || 'Job' });
          m.set(key, arr);
          cursor.setDate(cursor.getDate() + 1);
        }
      }
    }
    return m;
  }, [events]);

  const selEvents = eventsByDay.get(selectedDay) || [];
  // Selected day = every dated task covering the day, plus the day's jobs'
  // undated tasks (deduped — the same task can ride on several events).
  const selTasks = useMemo(() => {
    const seen = new Set<number>();
    const out: { task: NonNullable<ScheduleEvent['subtasks']>[number]; job: string; anyDay?: boolean }[] = [];
    for (const t of tasksByDay.get(selectedDay) || []) {
      if (!seen.has(t.task.id)) { seen.add(t.task.id); out.push(t); }
    }
    for (const e of eventsByDay.get(selectedDay) || []) {
      for (const st of e.subtasks || []) {
        if (day(st.start_date)) continue; // dated handled above
        if (!seen.has(st.id)) { seen.add(st.id); out.push({ task: st, job: e.job_title || 'Job', anyDay: true }); }
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksByDay, eventsByDay, selectedDay]);
  const selLabel = new Date(selectedDay + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' });

  if (loading) return <div className="h-24 animate-pulse rounded-xl bg-gray-100" />;
  if (error) return <div className="px-4 py-6 text-sm text-red-600 flex items-center gap-2"><span className="material-symbols-outlined text-base">error</span>{error}</div>;

  return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2 text-sm">
            <span className="material-symbols-outlined text-[#C77D3F] text-xl">calendar_month</span>
            My Schedule
          </h2>
          <CalendarSubscribe compact />
        </div>

        {/* Month navigation */}
        <div className="flex items-center justify-between">
          <button onClick={() => setMonthAnchor(p => new Date(p.getFullYear(), p.getMonth() - 1, 1))}
            className="h-10 w-10 rounded-lg border border-gray-200 text-gray-600 flex items-center justify-center active:bg-gray-100">
            <span className="material-symbols-outlined">chevron_left</span>
          </button>
          <button
            onClick={() => { const n = new Date(); setMonthAnchor(new Date(n.getFullYear(), n.getMonth(), 1)); setSelectedDay(today); }}
            className="text-base font-bold text-gray-900"
          >
            {MONTHS[curMonth]} {monthAnchor.getFullYear()}
          </button>
          <button onClick={() => setMonthAnchor(p => new Date(p.getFullYear(), p.getMonth() + 1, 1))}
            className="h-10 w-10 rounded-lg border border-gray-200 text-gray-600 flex items-center justify-center active:bg-gray-100">
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>

        {/* Month grid */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-7 text-center text-[11px] font-bold text-gray-400 uppercase border-b border-gray-100">
            {WEEKDAYS.map((w, i) => <div key={i} className="py-1.5">{w}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {weeks.flat().map(d => {
              const key = ymd(d);
              const inMonth = d.getMonth() === curMonth;
              const dayEvents = eventsByDay.get(key) || [];
              const isToday = key === today;
              const isSel = key === selectedDay;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedDay(key)}
                  className={`relative min-h-[4.5rem] flex flex-col items-stretch border-b border-r border-gray-50 last:border-r-0 p-0.5 pt-1 overflow-hidden
                    ${inMonth ? '' : 'opacity-30'}
                    ${isSel ? 'bg-[#C77D3F]/15' : 'active:bg-gray-50'}`}
                >
                  <span className={`self-center text-xs leading-none flex items-center justify-center rounded-full h-5 w-5 mb-0.5
                    ${isToday ? 'bg-[#C77D3F] text-white font-bold' : isSel ? 'text-[#C77D3F] font-bold' : 'text-gray-700'}`}>
                    {d.getDate()}
                  </span>
                  {/* Job chips — same idea as the office calendar's month cells */}
                  <span className="flex flex-col gap-0.5">
                    {dayEvents.slice(0, 2).map((e, i) => (
                      <span
                        key={`${e.id}-${i}`}
                        className={`block text-[8.5px] leading-tight font-semibold rounded px-0.5 py-px truncate text-left ${
                          e.id < 0 ? 'bg-[#C77D3F]/15 text-[#8a5526]' : 'bg-[#C77D3F] text-white'
                        }`}
                      >
                        {e.job_title || 'Job'}
                      </span>
                    ))}
                    {dayEvents.length > 2 && (
                      <span className="text-[8px] text-gray-400 text-left px-0.5">+{dayEvents.length - 2}</span>
                    )}
                    {(tasksByDay.get(key)?.length || 0) > 0 && (
                      <span className="flex items-center gap-0.5 px-0.5 text-[8.5px] font-bold text-[#7a4a22]">
                        <span className="material-symbols-outlined text-[9px] leading-none">task_alt</span>
                        {tasksByDay.get(key)!.filter(t => t.task.status === 'completed').length}/{tasksByDay.get(key)!.length}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-3 px-3 py-1.5 border-t border-gray-100 text-[10px] text-gray-500">
            <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-[#C77D3F]" /> Scheduled day</span>
            <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-[#C77D3F]/20" /> Job running</span>
          </div>
        </div>

        {/* Selected-day details */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-600">{selLabel}</h2>
          {deliveries.filter(d => String(d.expected_date).slice(0, 10) === selectedDay).map(d => (
            <div key={`del-${d.id}`} className="bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 flex items-center gap-2">
              <span className="material-symbols-outlined text-blue-500">local_shipping</span>
              <span className="text-sm text-blue-900 font-medium">
                Delivery: {d.po_number}{d.vendor ? ` — ${d.vendor}` : ''}{d.job_title ? ` (${d.job_title})` : ''}
              </span>
            </div>
          ))}
          {selTasks.length > 0 && (
            <div className="bg-[#FAF6F0] border border-[#C77D3F]/25 rounded-xl p-3">
              <p className="text-xs font-bold text-[#7a4a22] flex items-center gap-1.5 mb-1">
                <span className="material-symbols-outlined text-base text-[#C77D3F]">view_kanban</span>
                Planned tasks this day
              </p>
              <div className="space-y-1">
                {selTasks.map(({ task, job, anyDay }) => {
                  const awaiting = task.approval_status === 'pending';
                  return (
                    <div key={task.id} className="flex items-start gap-1.5 text-sm">
                      <span className={`material-symbols-outlined text-base mt-px ${task.status === 'completed' ? 'text-green-600' : awaiting ? 'text-amber-500' : 'text-gray-300'}`}>
                        {task.status === 'completed' ? 'check_circle' : awaiting ? 'hourglass_top' : 'radio_button_unchecked'}
                      </span>
                      <span className="min-w-0">
                        <span className={`leading-snug ${task.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                          {task.title}
                          {awaiting && <span className="ml-1.5 text-[9px] font-bold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5 align-middle">awaiting approval</span>}
                        </span>
                        <span className="block text-[10px] text-gray-400 truncate">{job}{task.start_time ? ` · ${String(task.start_time).slice(0, 5)}` : ''}{anyDay ? ' · any day this job runs' : ''}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5">Check these off when you log your hours.</p>
            </div>
          )}

          {/* Suggest a task (clearance-gated) — goes to the office for approval */}
          {canSuggest && suggestJobs.length > 0 && (
            <div>
              {sMsg && <p className={`text-xs mb-1 ${sMsg.startsWith('Sent') ? 'text-green-700' : 'text-red-600'}`}>{sMsg}</p>}
              {suggestOpen ? (
                <div className="bg-white rounded-xl border border-[#C77D3F]/40 p-3 space-y-1.5">
                  <input
                    autoFocus value={sTitle} onChange={e => setSTitle(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') sendSuggestion(selectedDay); if (e.key === 'Escape') setSuggestOpen(false); }}
                    placeholder="Task that should be on the plan…"
                    className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none"
                  />
                  <select value={sJobId ?? suggestJobs[0]?.id ?? ''} onChange={e => setSJobId(Number(e.target.value))}
                    className="w-full border border-gray-200 rounded-lg px-2 py-2 text-xs bg-white focus:outline-none">
                    {suggestJobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
                  </select>
                  <div className="flex gap-1.5">
                    <button onClick={() => sendSuggestion(selectedDay)} disabled={!sTitle.trim()}
                      className="flex-1 h-9 rounded-lg bg-[#C77D3F] text-white text-xs font-bold disabled:opacity-40">
                      Send for approval
                    </button>
                    <button onClick={() => setSuggestOpen(false)} className="h-9 px-3 rounded-lg border border-gray-200 text-xs text-gray-500">✕</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setSuggestOpen(true); setSMsg(''); }}
                  className="w-full h-9 rounded-xl border border-dashed border-[#C77D3F]/40 text-xs font-semibold text-[#C77D3F] flex items-center justify-center gap-1 active:bg-[#C77D3F]/5">
                  <span className="material-symbols-outlined text-base">add_task</span>
                  Suggest a task for this day
                </button>
              )}
            </div>
          )}
          {selEvents.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-5 text-center text-sm text-gray-400">
              No work scheduled this day
            </div>
          ) : (
            selEvents.map(e => (
              <div key={`${e.id}-${selectedDay}`} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm">{e.job_title || 'Job'}</p>
                    {e.start_time && <p className="text-xs text-gray-500 mt-0.5">{String(e.start_time).slice(0, 5)}{e.end_time ? ` – ${String(e.end_time).slice(0, 5)}` : ''}</p>}
                    {e.site_address && (
                      <button type="button" onClick={() => openMaps(e.site_address!)}
                        className="mt-1 flex items-center gap-1 text-xs text-[#C77D3F] active:opacity-70">
                        <span className="material-symbols-outlined text-xs">location_on</span>
                        <span className="truncate">{e.site_address}</span>
                      </button>
                    )}
                    {e.crew_name && <p className="text-xs text-gray-400 mt-0.5">Crew: {e.crew_name}</p>}
                    {/* Day tasks — same breakdown the office calendar shows */}
                    {e.subtasks && e.subtasks.filter(st => !day(st.start_date) || day(st.start_date) === selectedDay).length > 0 && (
                      <div className="mt-2 border-t border-gray-100 pt-2 space-y-1">
                        {e.subtasks.filter(st => !day(st.start_date) || day(st.start_date) === selectedDay).map(st => (
                          <div key={st.id} className="flex items-center gap-1.5 text-xs">
                            <span className={`material-symbols-outlined text-sm ${st.status === 'completed' ? 'text-green-600' : 'text-gray-300'}`}>
                              {st.status === 'completed' ? 'check_circle' : 'radio_button_unchecked'}
                            </span>
                            <span className={st.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-700'}>{st.title}</span>
                            {st.start_time && <span className="text-gray-400 ml-auto">{String(st.start_time).slice(0, 5)}{st.end_time ? `–${String(st.end_time).slice(0, 5)}` : ''}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {e.notes && <p className="text-xs text-gray-500 mt-2 border-t border-gray-100 pt-2">{e.notes}</p>}
                  </div>
                  <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium capitalize ${e.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : e.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {(e.status || 'scheduled').replace('_', ' ')}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Collapsible Gantt (timeline) */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => setGanttOpen(v => !v)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left"
          >
            <span className="material-symbols-outlined text-[#C77D3F] text-xl">view_timeline</span>
            <span className="font-semibold text-gray-900 text-sm flex-1">Month timeline</span>
            <span className={`material-symbols-outlined text-gray-400 transition-transform ${ganttOpen ? 'rotate-180' : ''}`}>expand_more</span>
          </button>

          {ganttOpen && (
            <div className="border-t border-gray-100">
              {ganttRows.length === 0 ? (
                <p className="p-4 text-sm text-gray-400">Nothing scheduled this month.</p>
              ) : (
                <div className="divide-y divide-gray-50">
                  {/* Stacked vertical list — one block per job, its month-span bar,
                      and the planned tasks underneath. No sideways scrolling. */}
                  {ganttRows.map(({ e, startDay, endDay }, ri) => {
                    const todayDate = new Date(today + 'T12:00:00');
                    const todayInMonth = todayDate.getMonth() === curMonth && todayDate.getFullYear() === monthAnchor.getFullYear();
                    // ALL of the event's tasks: dated ones first (any month — the
                    // label says when), undated ones last as "any day".
                    const monthTasks = [...(e.subtasks || [])].sort((a, b) => {
                      const da = day(a.start_date), db2 = day(b.start_date);
                      if (!da && !db2) return 0;
                      if (!da) return 1;
                      if (!db2) return -1;
                      return da.localeCompare(db2);
                    });
                    return (
                      <div key={`${e.id}-${ri}`} className="px-3 py-2.5">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-sm font-semibold text-gray-900 truncate">{e.job_title || 'Job'}</p>
                          <p className="text-[11px] text-gray-400 whitespace-nowrap">
                            {new Date(day(e.scheduled_date) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                            {day(e.end_date) && day(e.end_date) !== day(e.scheduled_date)
                              ? ` – ${new Date(day(e.end_date) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}` : ''}
                          </p>
                        </div>
                        {/* Month-scale span bar */}
                        <div className="relative h-2 bg-gray-100 rounded-full mt-1.5">
                          <div
                            className="absolute inset-y-0 rounded-full bg-[#C77D3F]/85"
                            style={{
                              left: `${((startDay - 1) / daysInMonth) * 100}%`,
                              width: `${Math.max(((endDay - startDay + 1) / daysInMonth) * 100, 3)}%`,
                            }}
                          />
                          {todayInMonth && (
                            <div className="absolute -inset-y-0.5 w-0.5 bg-[#2C1810]/60 rounded"
                              style={{ left: `${((todayDate.getDate() - 0.5) / daysInMonth) * 100}%` }} />
                          )}
                        </div>
                        {monthTasks.length > 0 && (
                          <div className="mt-1.5 space-y-0.5">
                            {monthTasks.map(st => (
                              <button key={st.id}
                                onClick={() => {
                                  const d = day(st.start_date);
                                  if (!d) return;
                                  const dd = new Date(d + 'T12:00:00');
                                  // Jump the calendar to the task's day, flipping months if needed
                                  if (dd.getMonth() !== curMonth || dd.getFullYear() !== monthAnchor.getFullYear()) {
                                    setMonthAnchor(new Date(dd.getFullYear(), dd.getMonth(), 1));
                                  }
                                  setSelectedDay(d);
                                }}
                                className="w-full flex items-center gap-1.5 text-left">
                                <span className={`material-symbols-outlined text-sm ${st.status === 'completed' ? 'text-green-600' : st.approval_status === 'pending' ? 'text-amber-500' : 'text-gray-300'}`}>
                                  {st.status === 'completed' ? 'check_circle' : st.approval_status === 'pending' ? 'hourglass_top' : 'radio_button_unchecked'}
                                </span>
                                <span className={`text-xs flex-1 truncate ${st.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{st.title}</span>
                                <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                  {(() => {
                                    const d = day(st.start_date);
                                    if (!d) return 'any day';
                                    const dd = new Date(d + 'T12:00:00');
                                    const sameMonth = dd.getMonth() === curMonth && dd.getFullYear() === monthAnchor.getFullYear();
                                    return dd.toLocaleDateString('en-CA', sameMonth ? { weekday: 'short', day: 'numeric' } : { month: 'short', day: 'numeric' });
                                  })()}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <p className="px-3 py-2 text-[10px] text-gray-400">Bar = when the job runs this month · dark tick = today · tap a task to jump to its day.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
  );
};

// Schedule lives on Home now — old links land there.
export const EmployeeSchedulePage = () => <Navigate to="/portal/employee" replace />;
